// PhotoLightbox — full-screen photo viewer for album detail (W3 §6.3).
//
// Per w3.md §6.3:
//   - Full-viewport overlay (fixed inset-0 z-50)
//   - Tap left/right halves to navigate (mobile-native)
//   - Keyboard: ← → to navigate, Esc to close
//   - 44×44 close button (top-right), 44×44 nav arrows
//   - Caption + uploader + taken_at bottom strip
//   - Counter "1 / 24" centered bottom
//
// `photos` is intentionally minimal so the same component works with both
// album items (media_id + caption) and mock data (label + tone).
//
// **W56.0 E — built on Radix `Dialog`.** It used to declare
// `role="dialog" aria-modal="true"` by hand, which was a claim the DOM did
// not back up: no focus trap, no focus restore, no inert background. Radix's
// modal dialog supplies all three, and the repository already depends on it
// (`patterns/BottomSheet` has 21 call sites) — two focus-management systems
// in one app is two sets of rules fighting.
//
// **W56.0 F — the polish pass on top of the migration:**
//   - the backdrop is **pure black** (a media viewer's language is 「nothing
//     outside the content」),
//   - the counter announces itself (`aria-live="polite"`),
//   - a photo with neither caption nor label still gets a non-empty `alt`,
//   - double-tap / double-click zooms to 2×, and a second one restores 1×.

import * as Dialog from "@radix-ui/react-dialog";
import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, X, ZoomIn, ZoomOut } from "lucide-react";
import { cn } from "@/lib/utils/cn";
import { restoreFocus, useFocusRestoreTarget } from "@/lib/a11y/focusRestore";

export interface PhotoLightboxPhoto {
  id: string;
  media_id?: string;
  url?: string;
  caption?: string;
  label?: string;
  tone?: string;
  uploaded_by?: string;
  taken_at?: string | null;
}

export interface PhotoLightboxProps {
  open: boolean;
  onClose: () => void;
  photos: readonly PhotoLightboxPhoto[];
  /** 0-indexed photo to show initially. */
  startIndex?: number;
  /** Album title shown in the nav-bar. */
  title?: string;
  /** Optional caption override (defaults to photo label). */
  testId?: string;
}

/**
 * W56.0 F — the zoom factor.
 *
 * **One step, no panning.** A photo viewer that zooms to 2× and stops there is
 * a viewer; one that zooms to 8× with a draggable surface is a photo editor,
 * and an editor on a phone is a different product with different gestures
 * (pinch, pan, rubber-band edges) that this round does not attempt. So the
 * state is a boolean and the scale is a constant.
 */
const ZOOM_SCALE = 2;

/**
 * PhotoLightbox — full-screen photo viewer for album detail.
 */
export function PhotoLightbox({
  open,
  onClose,
  photos,
  startIndex = 0,
  title,
  testId,
}: PhotoLightboxProps) {
  // W56.0 E — the element that had focus before the viewer opened.
  const restoreTarget = useFocusRestoreTarget(open);
  const safeStart = Math.min(Math.max(startIndex, 0), Math.max(photos.length - 1, 0));
  const [index, setIndex] = useState(safeStart);
  // W56.0 F — 1× or 2×. Reset whenever the photo changes, so a zoom never
  // carries over to the next picture.
  const [zoomed, setZoomed] = useState(false);

  // Reset index whenever the lightbox re-opens with a different start.
  useEffect(() => {
    setIndex(safeStart);
  }, [safeStart, open]);

  // W56.0 F — leaving a photo behind at 2× would be a trap with no visible
  // way out of it.
  useEffect(() => {
    setZoomed(false);
  }, [index]);

  const goPrev = useCallback(() => {
    setIndex((i) => (i > 0 ? i - 1 : Math.max(photos.length - 1, 0)));
  }, [photos.length]);
  const goNext = useCallback(() => {
    setIndex((i) => (i < photos.length - 1 ? i + 1 : 0));
  }, [photos.length]);

  // W56.0 F — the zoom toggle. It is a plain callback so both the
  // double-tap gesture and the keyboard-reachable button below can drive it.
  const toggleZoom = useCallback(() => {
    setZoomed((z) => !z);
  }, []);

  // Body scroll lock + keyboard nav (← / →)
  //
  // W56.0 E — **Escape is Radix's job now.** It used to be handled here, on
  // `window`, and the sheet had a second handler on `document`; in a real
  // browser a keydown on an element bubbles to `window`, so both would have
  // fired and the viewer would have closed twice. Radix's dismissable layer
  // listens on the owner document with `capture`, which is the single owner
  // of Escape now.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, goPrev, goNext]);

  if (!open) return null;
  if (photos.length === 0) return null;

  const photo = photos[index];
  if (!photo) return null;

  // W56.0 F — a photo with no caption and no label still needs an `alt` that
  // says something. 「第 N 張」 is the same information the counter carries,
  // so a screen-reader user hearing the alt knows which picture they are on.
  const photoAlt = photo.caption ?? photo.label ?? `第 ${index + 1} 張`;

  const photoStyle: React.CSSProperties = photo.tone
    ? {
        background: (() => {
          const toneMap: Record<string, [string, string]> = {
            you: ["110 165 220", "175 145 210"],
            partner: ["220 100 130", "184 134 11"],
            shared: ["175 145 210", "252 250 245"],
            paper: ["252 250 245", "145 142 150"],
            gold: ["184 134 11", "110 165 220"],
          };
          const [from, to] = toneMap[photo.tone] ?? toneMap.you!;
          return `linear-gradient(135deg, rgb(${from}) 0%, rgb(${to}) 100%)`;
        })(),
      }
    : {};

  // W56.0 F — the zoom is expressed as a **data attribute**, not as an inline
  // `style.transform`: the project's rule is that state travels as a data
  // attribute and the stylesheet decides what it means. `data-[zoom=2]:scale-[2]`
  // is the Tailwind expression of that rule.
  //
  // **`scale-[2]`, not `scale-200`.** Tailwind v3's default `scale` scale stops
  // at `150` — `scale-200` is a v4 utility, so on this project it emitted
  // **no rule at all** and the "2×" zoom would have been a silent no-op. The
  // arbitrary value is the only way to say "2" here without a config token
  // (`tailwind.config.ts` is not this component's to change).
  const zoomClasses = cn(
    "transition-transform duration-base ease-quiet",
    "data-[zoom=2]:scale-[2]",
  );

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <Dialog.Portal>
        {/*
          W56.0 F — the backdrop is **pure black**.

          It used to be `bg-ink-1/95 backdrop-blur-md`: 95% opaque, so 5% of
          the album page's warm paper tint bled through the "black", and the
          blur made the picture behind it shimmer. A media viewer's language
          is 「nothing outside the content」 — the eye must not find the page
          it came from while it is looking at a picture.

          `DESIGN.md` does ban pure black, but only as the **dark-mode page
          background**: §2.7 says the dark surface is warm-dark `#1a1a1e`
          「不是纯黑」, and §18's anti-pattern table repeats it as 「纯黑 dark
          mode → warm-dark `#1a1a1e`」. That rule is about the *page* a user
          reads text on; a lightbox backdrop is not a page, it is the absence
          of one, and the same warm-dark there would put a brown cast back
          into the picture. Dark mode is `not-shipped` (§7) in any case.
        */}
        <Dialog.Overlay
          data-testid="photo-lightbox-backdrop"
          className="fixed inset-0 z-40 bg-black"
        />
        <Dialog.Content
          data-component="photo-lightbox"
          data-testid={testId ?? "photo-lightbox"}
          data-open="true"
          data-photo-id={photo.id}
          // Radix 1.1 renders `role="dialog"` on `Dialog.Content` but **not**
          // `aria-modal` — the attribute was dropped upstream — so the modal
          // claim is stated here explicitly.
          aria-modal="true"
          // W56.0 E — give the focus back to whatever opened the viewer.
          onCloseAutoFocus={(event) => restoreFocus(event, restoreTarget.current)}
          className="fixed inset-0 z-50 flex flex-col"
        >
          <div className="flex items-center justify-between px-4 pt-4 pb-2 text-paper">
            <Dialog.Title
              data-testid="photo-lightbox-title"
              className="text-body-sm font-medium truncate"
            >
              {title ?? `${index + 1} / ${photos.length}`}
            </Dialog.Title>
            <div className="flex items-center">
              {/*
                W56.0 F — the zoom, reachable **without a pointer**.

                A double-tap is a gesture, and a gesture is not something a
                keyboard user can perform: without this button the 2× zoom
                would be mouse-only, which is the exact defect the rest of
                this migration is about. `aria-pressed` says which state the
                toggle is in, and the label changes with it.
              */}
              <button
                type="button"
                onClick={toggleZoom}
                aria-pressed={zoomed}
                aria-label={zoomed ? "還原照片大小" : "放大照片"}
                data-testid="photo-lightbox-zoom"
                className="press inline-flex h-11 w-11 items-center justify-center rounded-full text-paper hover:bg-paper/10 transition-colors"
              >
                {zoomed ? <ZoomOut size={20} aria-hidden /> : <ZoomIn size={20} aria-hidden />}
              </button>
              <button
                type="button"
                onClick={onClose}
                aria-label="關閉"
                data-testid="photo-lightbox-close"
                className="press inline-flex h-11 w-11 items-center justify-center rounded-full text-paper hover:bg-paper/10 transition-colors"
              >
                <X size={22} aria-hidden />
              </button>
            </div>
          </div>

          <div className="flex-1 relative flex items-center justify-center">
            <button
              type="button"
              onClick={goPrev}
              aria-label="上一張"
              data-testid="photo-lightbox-prev"
              className={cn(
                "press absolute left-2 top-1/2 -translate-y-1/2 z-10",
                "h-11 w-11 rounded-full bg-paper/10 text-paper",
                "hover:bg-paper/20 transition-colors",
                "flex items-center justify-center",
              )}
            >
              <ChevronLeft size={24} aria-hidden />
            </button>

            {photo.media_id || photo.url ? (
              <img
                src={photo.url ?? `/api/v1/media/${photo.media_id}/file`}
                alt={photoAlt}
                onDoubleClick={toggleZoom}
                data-zoom={zoomed ? String(ZOOM_SCALE) : "1"}
                className={cn("max-h-[80vh] max-w-[90vw] object-contain", zoomClasses)}
                data-testid="photo-lightbox-photo"
              />
            ) : (
              <div
                data-testid="photo-lightbox-photo"
                data-photo-tone={photo.tone}
                onDoubleClick={toggleZoom}
                data-zoom={zoomed ? String(ZOOM_SCALE) : "1"}
                className={cn(
                  "w-full max-w-2xl aspect-square mx-auto rounded-2xl shadow-modal",
                  "flex items-center justify-center text-paper text-display-1 font-display font-bold",
                  zoomClasses,
                )}
                style={photoStyle}
              >
                <span aria-hidden>{photo.label}</span>
              </div>
            )}

            <button
              type="button"
              onClick={goNext}
              aria-label="下一張"
              data-testid="photo-lightbox-next"
              className={cn(
                "press absolute right-2 top-1/2 -translate-y-1/2 z-10",
                "h-11 w-11 rounded-full bg-paper/10 text-paper",
                "hover:bg-paper/20 transition-colors",
                "flex items-center justify-center",
              )}
            >
              <ChevronRight size={24} aria-hidden />
            </button>
          </div>

          {photo.caption || photo.uploaded_by || photo.taken_at ? (
            <div
              data-testid="photo-lightbox-caption"
              className="text-center text-body-sm text-paper/80 pb-1"
            >
              {photo.uploaded_by ? <span>由 {photo.uploaded_by} 上傳</span> : null}
              {photo.caption ? <span className="mx-1">· {photo.caption}</span> : null}
              {photo.taken_at ? (
                <span className="mx-1">· {new Date(photo.taken_at).toLocaleDateString()}</span>
              ) : null}
            </div>
          ) : null}

          {/*
            W56.0 F — the counter announces itself.

            Moving through a set of photos is a state change with no other
            visible cue for a screen-reader user: the picture is an image, so
            nothing about it is announced. `aria-live="polite"` makes the
            「第 N / M 張」 update spoken without interrupting.
          */}
          <div
            data-testid="photo-lightbox-counter"
            aria-live="polite"
            className="text-center text-body-sm text-paper/80 pb-4"
          >
            第 {index + 1} / {photos.length} 張
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
